/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type { ComponentProps, MouseEvent } from "react"
import { forwardRef, useMemo } from "react"
import { format, subWeeks } from "date-fns"

import type { Issue, Project } from "@productlane/zero"
import { cn } from "@productlane/lib/src/browser"
import { formatRelativeTime } from "@productlane/lib/src/date"
import {
	Badge,
	Dot,
	ImportantIcon,
	SlaBreachIcon,
	SlaIcon,
	Tooltip,
} from "@productlane/ui"

import { getUnsnoozeBadgeContent } from "./unsnooze-badge-content"
import { getWaitingForReplyLabel } from "./waiting-badge-content"

const FilledClockIcon = forwardRef<SVGSVGElement, ComponentProps<"svg">>(
	({ className, ...props }, ref) => (
		<svg
			viewBox="0 0 14 14"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			ref={ref}
			{...props}
			className={className}
		>
			<circle cx="7" cy="7" r="6" fill="currentColor" />
			<path
				d="M7 4V7L9 8.5"
				className="stroke-gray-25 dark:stroke-gray-900"
				strokeWidth="1.25"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	),
)

const FilledCheckIcon = ({ className }: { className?: string }) => (
	<svg
		viewBox="0 0 14 14"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		className={className}
	>
		<circle cx="7" cy="7" r="6" fill="currentColor" />
		<path
			d="M4.5 7L6.25 8.75L9.5 5.25"
			stroke="white"
			strokeWidth="1.25"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
)

export const ResponseBadges = {
	WaitingClockIcon: ({ waitingSince }: { waitingSince?: Date | null }) => {
		const label = useMemo(
			() => getWaitingForReplyLabel(waitingSince),
			[waitingSince],
		)

		return (
			<Tooltip label={label}>
				<FilledClockIcon className="h-3 w-3 flex-none text-yellow-500 dark:text-yellow-400" />
			</Tooltip>
		)
	},
	WaitingClock: ({ time }: { time: Date }) => {
		const label = useMemo(() => getWaitingForReplyLabel(time), [time])

		return time > subWeeks(new Date(), 4) ? (
			<Tooltip label={label}>
				<Badge
					variant="default"
					className="rounded-full bg-yellow-50/50 py-1.5 pl-1.5 pr-2 text-[0.6875rem] font-normal text-yellow-700 ring-yellow-500/40 dark:bg-yellow-500/10 dark:text-yellow-400 dark:ring-yellow-500/20"
				>
					<FilledClockIcon className="h-[13px] w-[13px] text-yellow-500 dark:text-yellow-400" />
					<span>Waiting for response</span>
				</Badge>
			</Tooltip>
		) : null
	},
	Time: ({ time, isUnread }: { time: Date; isUnread?: boolean }) => {
		const formattedTime = useMemo(() => {
			return formatRelativeTime(time)
		}, [time])

		return (
			<Tooltip label={format(time, "EEE, MMM d 'at' h:mm a")}>
				<span
					className={cn(
						"hidden min-w-[2rem] sm:block",
						isUnread ? "text-secondary" : "text-tertiary",
					)}
				>
					{formattedTime}
				</span>
			</Tooltip>
		)
	},
	LiveChat: () => (
		<div className="flex items-center gap-2">
			<Dot color="green" className="animate-pulse" size="md" />
		</div>
	),
	CreatedAt: ({
		time,
		withCreated = false,
		isUnread,
	}: {
		time: Date
		withCreated?: boolean
		isUnread?: boolean
	}) => {
		const formattedTime = useMemo(() => {
			return formatRelativeTime(time)
		}, [time])

		return (
			<Tooltip label={format(time, "EEE, MMM d 'at' h:mm a")}>
				<div
					className={cn(
						"min-w-[1.8rem]",
						isUnread ? "text-secondary" : "text-tertiary",
					)}
				>
					{withCreated ? "Created " : ""}
					{formattedTime}
				</div>
			</Tooltip>
		)
	},
	Unsnoozed: () => (
		<Tooltip label="Unsnoozed thread">
			<div className="flex items-center">
				<Dot size="sm" className="[&>span:last-child]:bg-violet-500" />
			</div>
		</Tooltip>
	),
	UnsnoozedAt: ({ snoozedUntilIso }: { snoozedUntilIso: string }) => {
		const date = useMemo(() => {
			if (!snoozedUntilIso) return null
			const parsed = new Date(snoozedUntilIso)
			return isNaN(parsed.getTime()) ? null : parsed
		}, [snoozedUntilIso])

		const formattedDateForLabel = useMemo(() => {
			if (!date) return "Invalid date"
			return format(date, "EEE, MMM d 'at' h:mm a")
		}, [date])

		const badge = useMemo(() => {
			if (!snoozedUntilIso) return null
			return getUnsnoozeBadgeContent(snoozedUntilIso)
		}, [snoozedUntilIso])

		if (!date || !badge) {
			return null
		}

		return (
			<Tooltip
				label={
					badge.kind === "overdue"
						? `Unsnoozing (was due ${formattedDateForLabel})`
						: `Unsnoozes ${formattedDateForLabel}`
				}
			>
				<span className="flex-none whitespace-nowrap text-xs text-violet-600 dark:text-violet-400">
					{badge.label}
				</span>
			</Tooltip>
		)
	},
	Important: ({
		className,
		onClick,
	}: {
		className?: string
		onClick?: (e: MouseEvent<HTMLDivElement>) => void | Promise<void>
	}) => (
		<div
			className={cn("flex items-center", {
				"ml-0 mr-0": className,
			})}
			onClick={onClick}
		>
			<Badge
				variant="default"
				className={cn(
					"rounded-full bg-red-50/50 py-1.5 pl-1.5 pr-2 text-[0.6875rem] font-normal text-red-700 ring-red-600/20 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20",
					{
						"hover:bg-red-100 dark:hover:bg-red-600/20": onClick,
					},
				)}
			>
				<ImportantIcon className="ml-0.5 h-2.5 w-2.5 text-red-500 dark:text-red-400" />
				<span>Important</span>
			</Badge>
		</div>
	),
	SlaBreached: ({ timeText }: { timeText?: string }) => (
		<Tooltip label={timeText ? `Breached by ${timeText}` : "SLA breached"}>
			<SlaBreachIcon className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />
		</Tooltip>
	),
	SlaAtRisk: ({ timeText }: { timeText?: string }) => (
		<Tooltip label={timeText ? `${timeText} remaining` : "SLA at risk"}>
			<SlaIcon className="h-3.5 w-3.5 text-yellow-500 dark:text-yellow-400" />
		</Tooltip>
	),
	CloseLoop: ({
		issues,
		projects,
		className,
	}: {
		issues: readonly Issue[]
		projects: readonly Project[]
		className?: string
	}) => {
		const displayItem = useMemo(() => {
			const allItems = [
				...issues.map((i) => ({
					type: "issue" as const,
					title: i.title,
					completedAt: i.completedAt ?? 0,
				})),
				...projects.map((p) => ({
					type: "project" as const,
					title: p.name,
					completedAt: p.completedAt ?? 0,
				})),
			]

			if (allItems.length === 0) return null

			const completedItems = allItems.filter((item) => item.completedAt > 0)
			if (completedItems.length > 0) {
				return completedItems.reduce((mostRecent, current) =>
					current.completedAt > mostRecent.completedAt ? current : mostRecent,
				)
			}

			return allItems[0]
		}, [issues, projects])

		const label = displayItem
			? `Completed ${displayItem.type}: ${displayItem.title}`
			: "Completed"

		const title = displayItem?.title ?? "Completed"

		return (
			<div className={cn("flex items-center", className ?? "ml-0 mr-0")}>
				<Tooltip label={label}>
					<Badge
						variant="default"
						className="max-w-[280px] rounded-full bg-green-50/50 py-1.5 pl-1.5 pr-2 text-[0.6875rem] font-normal text-green-700 ring-green-500/40 dark:bg-green-500/10 dark:text-green-400 dark:ring-green-500/20"
					>
						<FilledCheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-green-500 dark:text-green-500" />
						<span className="truncate">{title}</span>
					</Badge>
				</Tooltip>
			</div>
		)
	},
}
